// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { MappedLink } from "@insite/content-library/Widgets/Header/MainNavigation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getIsMoblico } from "@mcmc/App/Store/Selectors/LayoutSelectors";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Transition, { TransitionStatus } from "react-transition-group/Transition";
import styled, { ThemeProps, withTheme } from "styled-components";
import { hideAll } from "tippy.js";

export interface McMcHeaderContextData {
    isSticky?: boolean;
    topMenus?: MappedLink[];
    updateTopMenus?: (topMenus: MappedLink[]) => void;
    headerHeight?: number;
    isMobileSearchOpen?: boolean;
    updateMobileSearchState?: (isOpen: boolean) => void;
    desktopHeaderHeight: number;
    mobileHeaderHeight: number;
    registerMenu?: () => void;
    unRegisterMenu?: () => void;
}

const mcMcHeaderContextData: McMcHeaderContextData = {
    topMenus: [],
    isSticky: false,
    isMobileSearchOpen: false,
    desktopHeaderHeight: 200,
    mobileHeaderHeight: 80,
};

export const McMcHeaderContext = React.createContext<McMcHeaderContextData>(mcMcHeaderContextData);

interface HeaderProviderComponentProps {
    children: React.ReactNode;
}

type Props = ThemeProps<BaseTheme> & HeaderProviderComponentProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: McMcApplicationState) => ({
    currentLocation: getLocation(state),
    isMoblico: getIsMoblico(state),
});

const ESC_KEY = 27;
export const HeaderProvider = ({ children, theme, currentLocation, isMoblico }: Props) => {
    const [isSticky, setIsSticky] = useState(false);
    const [topMenus, setTopMenus] = useState<MappedLink[]>([]);
    const [openedMenus, setOpenedMenus] = useState<number>(0);
    const updateTopMenus = (topMenus: MappedLink[]) => setTopMenus(topMenus);
    const stickyAnchor = useRef<HTMLDivElement>(null);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const updateMobileSearchState = (isOpen: boolean) => setIsMobileSearchOpen(isOpen);
    const isTablet = useCallback(
        () => window.matchMedia(`(max-width: ${theme.breakpoints.values[3] - 1}px)`).matches,
        [theme.breakpoints.values[3]],
    );

    const registerMenu = () => {
        setOpenedMenus(prevOpenedMenus => prevOpenedMenus + 1);
        hideAll();
    };
    const unRegisterMenu = () => setOpenedMenus(prevOpenedMenus => (prevOpenedMenus > 0 ? prevOpenedMenus - 1 : 0));

    const toggleSticky = useCallback(() => {
        if (stickyAnchor!.current!.getBoundingClientRect().bottom <= 0 || isTablet() || isMoblico) {
            setIsSticky(true);
        } else {
            setIsSticky(false);
        }
    }, [isTablet]);

    const handleResize = useCallback(() => {
        toggleSticky();
    }, [toggleSticky]);

    const onKeyDown = useCallback(event => {
        if (event.keyCode === ESC_KEY) {
            hideAll();
        }
    }, []);

    useEffect(() => {
        handleResize();

        window.addEventListener("scroll", toggleSticky, { passive: true });
        window.addEventListener("resize", handleResize);
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("scroll", toggleSticky);
            window.removeEventListener("resize", handleResize);
            window.addEventListener("keydown", onKeyDown);
        };
    }, []);

    useEffect(() => {
        setTimeout(() => hideAll());
    }, [isSticky, currentLocation]);

    return (
        <McMcHeaderContext.Provider
            value={{
                ...mcMcHeaderContextData,
                updateTopMenus,
                topMenus,
                isSticky,
                isMobileSearchOpen,
                updateMobileSearchState,
                registerMenu,
                unRegisterMenu,
            }}
        >
            <div style={{ zIndex: theme.zIndex.menu, position: "relative", breakAfter: "avoid"}} ref={stickyAnchor}>
                {children}
            </div>
            <Transition mountOnEnter unmountOnExit in={openedMenus > 0} timeout={{ enter: 75, exit: 250 }}>
                {state => <Overlay transitionState={state} />}
            </Transition>
        </McMcHeaderContext.Provider>
    );
};

export default connect(mapStateToProps)(withTheme(HeaderProvider));

const Overlay = styled.div<{ transitionState: TransitionStatus } & ThemeProps<BaseTheme>>`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 9;
    transition: opacity ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
`;
